class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    const isDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    const sectionId = isDrawer ? 'cart-drawer' : 'main-cart-items';

    return fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = isDrawer
          ? ['cart-drawer-items', '.cart-drawer__footer']
          : ['cart-items', '#main-cart-footer'];

        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        initCartRemoveButtonShow();
        initRecommendedProductEvents();
      })
      .catch(console.error);
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents-total',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const el = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        initCartRemoveButtonShow();
        initRecommendedProductEvents();

        let message = '';
        const updatedValue = parsedState.items[line - 1]?.quantity;
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          message = updatedValue === undefined
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }

        this.updateLiveRegions(line, message);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach(spinner => spinner.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => this.disableLoading(line));
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const container = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    container.classList.add('cart__items--disabled');

    const elements = [...this.querySelectorAll(`#CartItem-${line} .loading__spinner`),
                      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)];
    elements.forEach(el => el.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const container = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    container.classList.remove('cart__items--disabled');

    const elements = [...this.querySelectorAll(`#CartItem-${line} .loading__spinner`),
                      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)];
    elements.forEach(el => el.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

function initCartRemoveButtonShow() {
  document.querySelectorAll('.cart-remove-button__wrapper').forEach(wrapper => {
    const button = wrapper.querySelector('.show-btn');
    const activity = wrapper.querySelector('.activity');
    if (!button || !activity) return;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      activity.classList.add('show');
    });

    activity.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  });
}

document.addEventListener('click', () => {
  document.querySelectorAll('.cart-remove-button__wrapper .activity.show')
    .forEach(activity => activity.classList.remove('show'));
});

function initRecommendedProductEvents() {
  const wrapper = document.querySelector('.cart-drawer__recommended-products');
  if (!wrapper) return;

  wrapper.addEventListener('click', async (event) => {
    const button = event.target.closest('.quick-add-to-cart-button__plus-only-button');
    if (!button) return;

    const container = button.closest('.quick-add-to-cart-button');
    const variantId = container?.getAttribute('data-product-variant-id');
    if (!variantId) return;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) {
        const err = await res.json();
        console.error('Add to cart error:', err);
        return;
      }

      const secRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const json = await secRes.json();
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents) drawer.renderContents({ sections: json });

    } catch (error) {
      console.error('Request failed:', error);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCartRemoveButtonShow();
  initRecommendedProductEvents();
});
